/**
 * Insurance Error Guards
 *
 * Guards to check the type of insurance error and route to appropriate retry component
 */

import type { KioskMachineContext } from "../types";

/**
 * Guard metadata
 */
export interface GuardMetadata {
  displayName: string;
  description: string;
  category: "validation" | "context" | "timing" | "external";
}

/**
 * Guard with metadata wrapper
 */
export interface GuardWithMetadata {
  impl: (args: { context: KioskMachineContext }) => boolean;
  metadata: GuardMetadata;
}

/**
 * Helper function to extract insurance error message from context
 */
function getInsuranceErrorMessage(context: KioskMachineContext): string | null {
  // Check for error in insuranceError field (from verify-insurance actor)
  // biome-ignore lint/suspicious/noExplicitAny: Context type doesn't include error fields at compile time
  const insuranceError = (context as any).insuranceError;
  if (insuranceError?.insuranceError) {
    return insuranceError.insuranceError;
  }

  // Check for error in error field (from save-error action)
  // biome-ignore lint/suspicious/noExplicitAny: Context type doesn't include error fields at compile time
  const error = (context as any).error;
  if (error?.insuranceError) {
    return error.insuranceError;
  }

  // Check for raw message
  if (error?.message) {
    return error.message;
  }

  return null;
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function getInsuranceStates(context: KioskMachineContext): any {
  // biome-ignore lint/suspicious/noExplicitAny: Context type doesn't include error fields at compile time
  const contextAny = context as any;

  // Check both insuranceError and error fields (saveError action might use error field)
  const insuranceStates =
    contextAny.insuranceError?.insuranceStates ||
    contextAny.error?.insuranceStates;

  return insuranceStates;
}

/**
 * Check if error is a name-related error
 * Routes to FullNameInput component
 */
export const isNameError: GuardWithMetadata = {
  impl: ({ context }) => {
    const errorMessage = getInsuranceErrorMessage(context);
    if (!errorMessage) return false;

    const upperMessage = errorMessage.toUpperCase();
    return (
      upperMessage.includes("INVALID_NAME") ||
      upperMessage.includes("NAME_MISMATCH")
    );
  },
  metadata: {
    displayName: "isNameError",
    description:
      "Check if insurance error is related to name (INVALID_NAME, NAME_MISMATCH)",
    category: "context",
  },
};

/**
 * Check if error is a date of birth error
 * Routes to DOBInput component
 */
export const isDobError: GuardWithMetadata = {
  impl: ({ context }) => {
    const errorMessage = getInsuranceErrorMessage(context);
    if (!errorMessage) return false;

    const upperMessage = errorMessage.toUpperCase();
    return (
      upperMessage.includes("DOB_BAD_FORMAT") ||
      upperMessage.includes("DOB_MISMATCH")
    );
  },
  metadata: {
    displayName: "isDobError",
    description:
      "Check if insurance error is related to date of birth (DOB_BAD_FORMAT, DOB_MISMATCH)",
    category: "context",
  },
};

/**
 * Check if error is a member ID error
 * Routes to InsuranceInput component
 */
export const isMemberIdError: GuardWithMetadata = {
  impl: ({ context }) => {
    const errorMessage = getInsuranceErrorMessage(context);
    if (!errorMessage) return false;

    const upperMessage = errorMessage.toUpperCase();
    return (
      upperMessage.includes("MEMBERID_ERROR") ||
      upperMessage.includes("INVALID_MEMBERID") ||
      upperMessage.includes("MEMBERID_MISMATCH") ||
      upperMessage.includes("MEDICARE_MEMBERID_ERROR")
    );
  },
  metadata: {
    displayName: "isMemberIdError",
    description: "Check if insurance error is related to member ID",
    category: "context",
  },
};

/**
 * Helper function to check if the error is from secondary insurance
 */
function isSecondaryInsuranceFailed(context: KioskMachineContext): boolean {
  // biome-ignore lint/suspicious/noExplicitAny: Context type doesn't include error fields at compile time
  const contextAny = context as any;

  // Check both insuranceError and error fields (saveError action might use error field)
  const insuranceStates =
    contextAny.insuranceError?.insuranceStates ||
    contextAny.error?.insuranceStates;

  if (!insuranceStates) {
    return false;
  }

  const { primary, secondary } = insuranceStates;

  if (!secondary) {
    return false;
  }

  // Secondary error if primary is verified/active/in-network but secondary is not
  const result =
    primary.verified &&
    primary.active &&
    primary.inNetwork &&
    (!secondary.verified || !secondary.active || !secondary.inNetwork);

  return result;
}

/**
 * Check if error is from secondary insurance (for member ID errors)
 * Routes to secondary insurance edit
 */
export const isSecondaryMemberIdError: GuardWithMetadata = {
  impl: ({ context }) => {
    const isMemberId = isMemberIdError.impl({ context });
    const isSecondary = isSecondaryInsuranceFailed(context);
    const result = isMemberId && isSecondary;

    return result;
  },
  metadata: {
    displayName: "isSecondaryMemberIdError",
    description: "Check if member ID error is from secondary insurance",
    category: "context",
  },
};

/**
 * Check if error is from secondary insurance (for general errors)
 * Routes to secondary insurance edit
 */
export const isSecondaryInsuranceError: GuardWithMetadata = {
  impl: ({ context }) => {
    const result = isSecondaryInsuranceFailed(context);
    return result;
  },
  metadata: {
    displayName: "isSecondaryInsuranceError",
    description: "Check if error is from secondary insurance",
    category: "context",
  },
};

export const isOONInsurance: GuardWithMetadata = {
  impl: ({ context }) => {
    const insuranceStates = getInsuranceStates(context);
    if (!insuranceStates) return false;

    const { primary, secondary } = insuranceStates;

    const primaryActive = primary?.verified === true;
    const oonPrimary = primary?.errorCode === "OUT_OF_NETWORK";
    const oonSecondary = secondary?.errorCode === "OUT_OF_NETWORK";

    // If primary is active and secondary is OON, treat as insured with primary (ignore secondary)
    if (primaryActive && oonSecondary) {
      return false;
    }

    // If primary is OON, treat as OON
    if (oonPrimary) {
      return true;
    }

    // Otherwise, check secondary
    return oonSecondary;
  },
  metadata: {
    displayName: "isOONInsurance",
    description:
      "Check if insurance error is related to out-of-network insurance",
    category: "context",
  },
};
